/**
 * GreenSyn Inventory — reading a taxpayer's details from the GST portal.
 *
 * The portal (services.gst.gov.in) offers a public "Search Taxpayer" page that
 * needs no login, but it guards the search with a **captcha**: an image plus an
 * HttpOnly `CaptchaCookie` that must come back with the answer. So a lookup is
 * two calls, and the captcha is typed by a person — there is no server-side way
 * to read the image.
 *
 * This module is the *pure* half of that flow: the portal's URLs and codes, how
 * the captcha cookie is found in `Set-Cookie`, and how the portal's response is
 * turned into either details or a named failure. It does no I/O, and nothing
 * here is allowed to compute tax: these are reference fields, the same ones
 * `suppliers` stores, so that a purchase order and a certificate can be compared
 * by eye. See PLAN.md §28 and §29.
 *
 * The I/O half lives in `apps/api/src/services/gstLookup.ts`, because a browser
 * cannot make these calls itself: the portal sends `Access-Control-Allow-Origin: *`
 * together with `Access-Control-Allow-Credentials: true`, which is illegal for a
 * credentialed request, and its preflight answers 403 — so the captcha cookie can
 * never be obtained and the search is never sent. Only the server can ask.
 */

import { GST_REGISTRATION_TYPES, type GstRegistrationType } from './enums.js';
import {
  type GstStateCode,
  gstStateCodeFromGstin,
  gstStateName,
  normaliseGstin,
} from './gst.js';

/** The captcha image. `rnd` is added per call so neither browser nor proxy caches it. */
export const GST_CAPTCHA_URL = 'https://services.gst.gov.in/services/captcha?rnd=';
/** The no-login taxpayer search, POSTed `{ gstin, captcha }`. */
export const GST_TAXPAYER_URL = 'https://services.gst.gov.in/services/api/search/taxpayerDetails';
/** The cookie the captcha sets and the search must echo. */
export const GST_CAPTCHA_COOKIE = 'CaptchaCookie';
/** The captcha is always six digits (the portal's own input is `[0-9]{6}`). */
export const GST_CAPTCHA_PATTERN = /^[0-9]{6}$/;

/**
 * The portal's error codes, named. The codes are stable identifiers the portal
 * has used for years; the messages beside them are ours and meant to be read by
 * somebody onboarding a supplier.
 */
export const GST_PORTAL_ERRORS = {
  SWEB_9000: 'invalid_captcha',
  SWEB_9032: 'invalid_gstin',
  SWEB_9035: 'invalid_gstin',
  SWEB_9034: 'gstin_mandatory',
  SWEB_9021: 'service_busy',
  SWEB_8000: 'service_error',
} as const;

export type GstLookupErrorCode =
  | 'invalid_gstin'
  | 'invalid_captcha'
  | 'gstin_mandatory'
  | 'service_busy'
  | 'service_error'
  /** A 200 whose body was neither details nor a code we know. */
  | 'unrecognised_response';

export interface GstLookupError {
  code: GstLookupErrorCode;
  message: string;
}

/**
 * What the portal says about a taxpayer, trimmed to the fields a supplier record
 * cares about and renamed to match it. `NA` — the portal's word for "not
 * applicable" — becomes `null`, so the UI shows a dash rather than the string.
 */
export interface GstLookupDetails {
  gstin: string;
  legalName: string | null;
  tradeName: string | null;
  /** Active, Cancelled, Suspended, Inactive … */
  status: string | null;
  /** The portal's taxpayer type, e.g. "Regular", "SEZ Unit". */
  taxpayerType: string | null;
  constitutionOfBusiness: string | null;
  /** As printed on the certificate, `dd/mm/yyyy`. */
  registrationDate: string | null;
  natureOfBusiness: string[];
  principalPlaceOfBusiness: string | null;
  /** Derived from the GSTIN's first two digits — the same rule the API uses to fill it. */
  gstStateCode: GstStateCode | null;
  gstStateName: string | null;
  /**
   * Mapped from the taxpayer type, and only for the two the supplier record
   * knows. Anything else is `null` — *not* `unregistered`: a taxpayer type we do
   * not model is not a statement that they are unregistered.
   */
  gstRegistrationType: GstRegistrationType | null;
}

export type GstLookupOutcome =
  | { ok: true; details: GstLookupDetails }
  | { ok: false; error: GstLookupError };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A portal text field: blank and the sentinel `NA` both mean "not recorded". */
function portalText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 || trimmed === 'NA' ? null : trimmed;
}

/**
 * The `CaptchaCookie` value from a `Set-Cookie` header (or several).
 *
 * The portal sends one cookie on the captcha and clears it on the search, so
 * this is only ever read from the captcha response. A cleared value (`""`) and a
 * missing cookie both come back as the empty string, which the caller treats as
 * "no captcha obtained" rather than sending an empty one.
 */
export function extractCaptchaCookie(setCookie: string | readonly string[] | null | undefined): string {
  const headers = setCookie == null ? [] : typeof setCookie === 'string' ? [setCookie] : setCookie;

  for (const header of headers) {
    for (const part of header.split(';')) {
      const eq = part.indexOf('=');
      if (eq === -1) continue;
      if (part.slice(0, eq).trim() !== GST_CAPTCHA_COOKIE) continue;
      const value = part.slice(eq + 1).trim().replace(/^"(.*)"$/, '$1');
      if (value.length > 0) return value;
    }
  }

  return '';
}

/** The portal reports failures as a top-level `errorCode`, or nested under `error.error_cd`. */
function portalErrorCode(body: Record<string, unknown>): string | null {
  if (typeof body.errorCode === 'string') return body.errorCode;
  const nested = body.error;
  if (isRecord(nested) && typeof nested.error_cd === 'string') return nested.error_cd;
  return null;
}

const ERROR_MESSAGES: Record<GstLookupErrorCode, string> = {
  invalid_gstin: 'The GST portal does not recognise that GSTIN — check it against the certificate',
  invalid_captcha: 'That captcha was wrong or has expired — a new one is shown below',
  gstin_mandatory: 'The GST portal needs a GSTIN to search',
  service_busy: 'The GST portal is busy right now — try again in a moment',
  service_error: 'The GST portal reported a problem with that search',
  unrecognised_response: 'The GST portal returned a response this app did not understand',
};

/** True only for the registration types the supplier record actually models. */
function registrationTypeFromPortal(taxpayerType: string | null): GstRegistrationType | null {
  if (!taxpayerType) return null;
  const wanted = taxpayerType.trim().toLowerCase();
  return (
    GST_REGISTRATION_TYPES.find((type) => type === wanted) ?? null
  );
}

/**
 * Turns the portal's taxpayer payload into our `GstLookupDetails`. Exported for
 * the service's tests; callers normally go through `parseGstLookupResponse`.
 */
export function toGstLookupDetails(body: Record<string, unknown>): GstLookupDetails {
  const gstin = normaliseGstin(String(body.gstin));
  const stateCode = gstStateCodeFromGstin(gstin);
  const pradr = isRecord(body.pradr) ? body.pradr : null;

  return {
    gstin,
    legalName: portalText(body.lgnm),
    tradeName: portalText(body.tradeNam),
    status: portalText(body.sts),
    taxpayerType: portalText(body.dty),
    constitutionOfBusiness: portalText(body.ctb),
    registrationDate: portalText(body.rgdt),
    natureOfBusiness: Array.isArray(body.nba)
      ? body.nba.map(portalText).filter((entry): entry is string => entry !== null)
      : [],
    principalPlaceOfBusiness: pradr ? portalText(pradr.adr) : null,
    gstStateCode: stateCode,
    gstStateName: gstStateName(stateCode),
    gstRegistrationType: registrationTypeFromPortal(portalText(body.dty)),
  };
}

/**
 * Reads a 200 from the taxpayer search as either details or a named failure.
 *
 * Success is decided by the presence of a `gstin` in the body, not by the
 * absence of the portal's `message: null` — that marker is on errors, but
 * keying on it would break the moment the portal omitted it, and a lookup that
 * silently returns an empty record is exactly the failure this feature exists
 * to prevent.
 */
export function parseGstLookupResponse(body: unknown): GstLookupOutcome {
  if (!isRecord(body)) {
    return { ok: false, error: { code: 'unrecognised_response', message: ERROR_MESSAGES.unrecognised_response } };
  }

  const code = portalErrorCode(body);
  if (code) {
    const named = (GST_PORTAL_ERRORS as Record<string, GstLookupErrorCode>)[code];
    const errorCode: GstLookupErrorCode = named ?? 'service_error';
    return { ok: false, error: { code: errorCode, message: ERROR_MESSAGES[errorCode] } };
  }

  if (typeof body.gstin !== 'string' || body.gstin.trim().length === 0) {
    return { ok: false, error: { code: 'unrecognised_response', message: ERROR_MESSAGES.unrecognised_response } };
  }

  return { ok: true, details: toGstLookupDetails(body) };
}

/* -------------------------------------------------------------------------- */
/* Registration date                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The portal prints its registration date as `dd/mm/yyyy`; the supplier record
 * stores ISO. Normalising on the way in means storage and display cannot disagree
 * about the day, and a value that is not a real calendar date (`31/02/2017`) is
 * dropped rather than stored as something that merely looks like one.
 */
export function gstRegistrationDateToIso(value: string | null | undefined): string | null {
  if (!value) return null;

  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!match) return null;

  const day = Number(match[1]!);
  const month = Number(match[2]!);
  const year = Number(match[3]!);

  // Round-trips through Date to reject days the month does not have.
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return `${match[3]!}-${match[2]!}-${match[1]!}`;
}

/** The inverse, for showing a stored ISO date the way the certificate prints it. */
export function formatGstRegistrationDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!match) return null;
  return `${match[3]!}/${match[2]!}/${match[1]!}`;
}
